import { createContext, ReactElement } from 'react';
import { jwtDecode } from 'jwt-decode';
import api from 'utils/api';
import { ApiBase } from 'config';

// project import
import config, { MenuOrientation, ThemeDirection, ThemeMode } from '../config';
import useLocalStorage from '../hooks/useLocalStorage';

// types
import { CustomizationProps, FontFamily, I18n, PresetColor } from '../types/config';

// initial state
const initialState: CustomizationProps = {
    ...config,
    onChangeContainer: () => { },
    onChangeLocalization: () => { },
    onChangeMode: () => { },
    onChangePresetColor: () => { },
    onChangeDirection: () => { },
    onChangeMiniDrawer: () => { },
    onChangeMenuOrientation: () => { },
    onChangeFontFamily: () => { },
    setLibraryTheme: () => { }
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
    children: ReactElement;
};

const getLibraryId = (): string => {
    try {
        const token = localStorage.getItem('serviceToken');
        if (token) {
            const decoded: { LibraryId?: number } = jwtDecode(token);
            if (decoded && decoded.LibraryId) {
                return decoded.LibraryId.toString();
            }
        }
    } catch (e) {
        console.error("Error decoding token:", e);
    }
    return "global";
};

function ConfigProvider({ children }: ConfigProviderProps) {
    const libraryId = getLibraryId();
    const [config, setConfig] = useLocalStorage(`mantis-react-ts-config-lib-${libraryId}`, initialState);

    const onChangeContainer = () => {
        setConfig({
            ...config,
            container: !config.container
        });
    };

    const onChangeLocalization = (lang: I18n) => {
        setConfig({
            ...config,
            i18n: lang
        });
    };

    const onChangeMode = (mode: ThemeMode) => {
        setConfig({
            ...config,
            mode
        });
    };

    const saveTheme = (theme: string, font: string) => {
        api.post(`${ApiBase}/api/library/updateThemeSettings`, {
            themeColor: theme,
            fontFamily: font
        }).catch(err => console.error("Error updating theme settings:", err));
    };

    const onChangePresetColor = (theme: PresetColor) => {
        setConfig({
            ...config,
            presetColor: theme
        });
        saveTheme(theme, config.fontFamily);
    };

    const onChangeDirection = (direction: ThemeDirection) => {
        setConfig({
            ...config,
            themeDirection: direction
        });
    };

    const onChangeMiniDrawer = (miniDrawer: boolean) => {
        setConfig({
            ...config,
            miniDrawer
        });
    };

    const onChangeMenuOrientation = (layout: MenuOrientation) => {
        setConfig({
            ...config,
            menuOrientation: layout
        });
    };

    const onChangeFontFamily = (fontFamily: FontFamily) => {
        setConfig({
            ...config,
            fontFamily
        });
        saveTheme(config.presetColor, fontFamily);
    };

    const setLibraryTheme = (theme: PresetColor, fontFamily: FontFamily) => {
        setConfig({
            ...config,
            presetColor: theme,
            fontFamily
        });
    };

    return (
        <ConfigContext.Provider
            value={{
                ...config,
                onChangeContainer,
                onChangeLocalization,
                onChangeMode,
                onChangePresetColor,
                onChangeDirection,
                onChangeMiniDrawer,
                onChangeMenuOrientation,
                onChangeFontFamily,
                setLibraryTheme
            }}
        >
            {children}
        </ConfigContext.Provider>
    );
}

export { ConfigProvider, ConfigContext };
